import { db, LocalPlaylist } from '../db/index.js';
import {
  getPlaylistsToPush,
  getPlaylistTombstones,
  hardDeleteLocalPlaylist,
  markPlaylistPushed,
  ensureCloudTabMeta
} from '../db/operations.js';
import { apiClient, ApiError, ApiPlaylist, PlaylistUpsertBody } from '../utils/apiClient.js';
import { useAuthStore } from '../stores/authStore.js';
import { isUuid, newId } from '../utils/id.js';
import { mergePendingTabIds } from '../utils/playlistMerge.js';
import { LIBRARY_CHANGED_EVENT } from './events.js';

/**
 * Playlist sync (FRE-143), same shape as routineSync: push tombstones, push dirty playlists with the full ordered
 * tab list (PUT /api/playlists/:id), then pull. Latest edit wins; an edit brings back a playlist deleted elsewhere.
 */

let inFlight: Promise<void> | null = null;
let rerunRequested = false;

const statusOf = (err: unknown): number | undefined => (err instanceof ApiError ? err.status : undefined);

export function toUpsertBody(pl: LocalPlaylist): PlaylistUpsertBody {
  return { title: pl.title, tab_ids: pl.tabIds.filter(isUuid), created_at: pl.createdAt };
}

async function pushDeletes(uid: string): Promise<void> {
  for (const pl of await getPlaylistTombstones(uid)) {
    try {
      await apiClient.delete(`/api/playlists/${pl.id}`);
      await hardDeleteLocalPlaylist(pl.id);
    } catch (err) {
      if (statusOf(err) === 404) {
        await hardDeleteLocalPlaylist(pl.id);
        continue;
      }
      throw err;
    }
  }
}

async function rekeyPlaylist(oldId: string): Promise<void> {
  await db.transaction('rw', db.playlists, async () => {
    const cur = await db.playlists.get(oldId);
    if (!cur) return;
    await db.playlists.delete(oldId);
    const next: LocalPlaylist = { ...cur, id: newId(), dirty: 1, updatedAt: new Date().toISOString() };
    delete next.serverUpdatedAt;
    await db.playlists.put(next);
  });
}

async function pushUpserts(uid: string, token: string): Promise<void> {
  for (const pl of await getPlaylistsToPush(uid)) {
    const auth = useAuthStore.getState();
    if (auth.token !== token || auth.user?.id !== uid) return; // account changed: abort
    if (!isUuid(pl.id)) {
      console.warn('playlistSync: skipping playlist with non-UUID id', pl.id);
      continue;
    }
    if (pl.pushRejectedAt && pl.pushRejectedAt === pl.updatedAt) continue;
    const snapshot = pl.updatedAt;
    try {
      const res = await apiClient.put<ApiPlaylist>(`/api/playlists/${pl.id}`, toUpsertBody(pl));
      if (!res || res.id !== pl.id || typeof res.updatedAt !== 'string') {
        throw new Error('Unexpected playlist response'); // transient: e.g. a catch-all mock
      }
      const outcome = await markPlaylistPushed(pl.id, snapshot, res.updatedAt, uid);
      if (outcome === 'gone') {
        try {
          await apiClient.delete(`/api/playlists/${pl.id}`);
        } catch (err) {
          if (statusOf(err) !== 404) throw err;
        }
      }
    } catch (err) {
      const status = statusOf(err);
      if (status === 409) {
        await rekeyPlaylist(pl.id);
        continue;
      }
      if (status === 400) {
        console.warn('playlistSync: server rejected playlist', pl.id);
        await db.playlists.update(pl.id, { pushRejectedAt: snapshot });
        continue;
      }
      throw err;
    }
  }
}

async function pullPlaylists(uid: string): Promise<void> {
  const before = await db.playlists.toArray();
  const candidates = new Map<string, string>();
  for (const l of before) {
    if (l.ownerId === uid && l.dirty === 0 && !l.deletedAt && l.serverUpdatedAt) {
      candidates.set(l.id, l.serverUpdatedAt);
    }
  }

  const server = await apiClient.get<ApiPlaylist[]>('/api/playlists');
  if (!Array.isArray(server)) return;
  const serverIds = new Set(server.map((s) => s.id));

  let changed = false;
  await db.transaction('rw', [db.playlists, db.tabs], async () => {
    const pendingTabIds = new Set<string>(
      (await db.tabs.where('pendingUpload').equals(1).toArray()).map((t) => t.id)
    );
    for (const s of server) {
      const items = [...(s.items ?? [])].sort((a, b) => a.orderIndex - b.orderIndex);
      for (const item of items) {
        if (item.tab && (await ensureCloudTabMeta(item.tab, uid))) changed = true;
      }
      const tabIds = items.map((i) => i.tabId);
      const fromServer: LocalPlaylist = {
        id: s.id,
        ownerId: uid,
        title: s.title,
        createdAt: s.createdAt,
        tabIds,
        updatedAt: s.updatedAt,
        serverUpdatedAt: s.updatedAt,
        dirty: 0
      };
      const local = await db.playlists.get(s.id);
      if (!local) {
        await db.playlists.put(fromServer);
        changed = true;
        continue;
      }
      if (local.deletedAt || local.dirty === 1) continue;
      if (local.ownerId && local.ownerId !== uid) continue;
      if (local.serverUpdatedAt !== s.updatedAt) {
        const merged = mergePendingTabIds(tabIds, local.tabIds, (id) => pendingTabIds.has(id));
        await db.playlists.put({
          ...fromServer,
          tabIds: merged.ids,
          ...(merged.preserved && { dirty: 1 as const, updatedAt: new Date().toISOString() })
        });
        changed = true;
      }
    }

    for (const [id, snapshot] of candidates) {
      if (serverIds.has(id)) continue;
      const cur = await db.playlists.get(id);
      if (cur && cur.dirty === 0 && !cur.deletedAt && cur.ownerId === uid && cur.serverUpdatedAt === snapshot) {
        await db.playlists.delete(id);
        changed = true;
      }
    }
  });

  if (changed) window.dispatchEvent(new Event(LIBRARY_CHANGED_EVENT));
}

async function runOnce(): Promise<void> {
  const { token, isGuest, user } = useAuthStore.getState();
  if (!token || isGuest || !user) return;
  await pushDeletes(user.id);
  await pushUpserts(user.id, token);
  await pullPlaylists(user.id);
}

/** Push then pull. Single in-flight run; a call during a run schedules exactly one more. Guests: no-op. */
export function syncPlaylists(): Promise<void> {
  if (inFlight) {
    rerunRequested = true;
    return inFlight;
  }
  inFlight = (async () => {
    try {
      do {
        rerunRequested = false;
        await runOnce();
      } while (rerunRequested);
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/** Fire and forget; no-op offline; errors are swallowed. */
export function requestPlaylistSync(): void {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  syncPlaylists().catch((err) => {
    console.warn('playlistSync: background sync failed', err);
  });
}
